import React, { useEffect, useState } from "react";
import {
  reduxForm,
  FormSection,
  getFormValues,
  getFormSyncErrors
} from "redux-form";
import AddressSellCheckout from "@components/forms/AddressSellCheckout";
import SellContactInformation from "./SellContactInformation";
import SecurityImages from "./SecurityImages";
import SellCardsDetails from "./SellCardsDetails";
import SellOrderSummary from "./SellOrderSummary";
import { completeSellOrder } from "@actions/sellOrders";
import { defaultAction } from "@actions/defaultAction";
import { postPrimaryAddress } from "@actions/customerInfo";
import { DISPLAY_MAIN_PAYMENT_ERROR } from "@constants/actionTypes";
import loadable from "@loadable/component";
const SellVerificationCC = loadable(() =>
  import(
    /* webpackChunkName: "SellVerificationCC" */ "./forms/SellVerificationCC"
  )
);
import EnterACH from "@components/forms/EnterACH";
import MainContentSellContainer from "./MainContentSellContainer";
import SellFormDesktopBottomButton from "./SellFormDesktopBottomButton";
import { verifyCC } from "@utils/validators/verifyCC";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

const ACHSellWorkflow = ({
  handleSubmit,
  submitFailed,
  dispatch,
  change,
  valid
}) => {
  const history = useHistory();
  const regularReduxDispatch = useDispatch();
  const missingInfo = useSelector(state => state.customerInfo.missingInfo);
  const phone = useSelector(state => state.customerInfo.phone);
  const mainPaymentError = useSelector(
    state => state.customerInfo.mainPaymentError
  );
  const cardKnoxState = useSelector(state => state.cardKnox);
  const customerGetStatus = useSelector(
    state => state.customerInfo.customerGetStatus
  );
  const achSell = useSelector(state => getFormValues("achSell")(state));
  const achSellErrors = useSelector(state =>
    getFormSyncErrors("achSell")(state)
  );

  const [displayPrimary, setDisplayPrimary] = useState(null);
  const [phoneCompleted, changePhoneCompleted] = useState(!!phone);
  const [openPrimaryForm, changeOpenPrimaryForm] = useState(false);
  const [openAchForm, changeOpenAchForm] = useState(false);
  const [openVerificationForm, changeOpenVerificationForm] = useState(false);
  const [openToEdit, changeOpenToEdit] = useState(false);

  const achCompleted = () => {
    if (achSell?.enterACH?.achRow?.paymentDetails) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (customerGetStatus === "success") {
      if (!missingInfo) {
        setDisplayPrimary(false);
      } else {
        setDisplayPrimary(true);
      }
    }
  }, [customerGetStatus]);

  useEffect(() => {
    if (!missingInfo) {
      changeOpenToEdit(true);
    }
  }, [missingInfo]);

  useEffect(() => {
    if (submitFailed && !mainPaymentError) {
      window.scrollTo(0, 0);
      regularReduxDispatch(
        defaultAction(
          DISPLAY_MAIN_PAYMENT_ERROR,
          "Please fix the errors and try again"
        )
      );
    }
  }, [submitFailed]);

  useEffect(() => {
    if (phoneCompleted) {
      if (missingInfo) {
        changeOpenPrimaryForm(true);
      }
    }
  }, [phoneCompleted]);

  useEffect(() => {
    if (openPrimaryForm) {
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
      if (openAchForm) {
        changeOpenAchForm(false);
      }
    }

    if (!openPrimaryForm) {
      if (!missingInfo) {
        changeOpenAchForm(true);
      }
    }
  }, [openPrimaryForm]);

  useEffect(() => {
    if (openVerificationForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
      if (openAchForm) {
        changeOpenAchForm(false);
      }
    }
  }, [openVerificationForm]);

  useEffect(() => {
    if (openAchForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
    }
    if (!openAchForm) {
      if (achCompleted()) {
        changeOpenVerificationForm(true);
      }
    }
  }, [openAchForm]);

  const submitMailCheckOrder = handleSubmit(() => {
    regularReduxDispatch(completeSellOrder(history, "achSell"));
  });

  const primaryContinueAction = async () => {
    const response = await regularReduxDispatch(
      postPrimaryAddress(achSell.primaryResidence)
    );
    return response;
  };

  return (
    <form onSubmit={submitMailCheckOrder}>
      {achSell && Object.keys(achSell).length > 0 && (
        <MainContentSellContainer>
          <div>
            <SellCardsDetails />
            <SellContactInformation
              errors={achSellErrors}
              changePhoneCompleted={changePhoneCompleted}
              phoneValue={achSell?.newPhone}
              primaryResidence={
                displayPrimary ? null : achSell.primaryResidence
              }
            />
            {displayPrimary && (
              <FormSection name="primaryResidence">
                <AddressSellCheckout
                  heading="Primary Residence"
                  phoneCompleted={phoneCompleted}
                  errors={achSellErrors.primaryResidence}
                  addressLineTwo={achSell?.primaryResidence?.street2}
                  formContent={achSell?.primaryResidence}
                  openForm={openPrimaryForm}
                  changeOpenForm={changeOpenPrimaryForm}
                  flowNumber={"2"}
                  openToEdit={openToEdit}
                  continueAction={primaryContinueAction}
                  displayPrimary={displayPrimary}
                />
              </FormSection>
            )}
            <FormSection name="enterACH">
              <EnterACH
                openForm={openAchForm}
                missingInfo={missingInfo || !achCompleted()}
                errors={achSellErrors.enterACH}
                formContent={achSell?.enterACH}
                flowNumber={displayPrimary ? "3" : "2"}
                openToEdit={openToEdit}
                changeOpenForm={changeOpenAchForm}
                changeReduxField={change}
                achCompleted={achCompleted}
              />
            </FormSection>
            <FormSection name="verification">
              <SellVerificationCC
                openForm={openVerificationForm}
                heading={"Identity Verification"}
                errors={achSellErrors.verification}
                formContent={achSell?.verification}
                openToEdit={openToEdit}
                missingInfo={missingInfo || !achCompleted()}
                changeOpenForm={changeOpenVerificationForm}
                flowNumber={displayPrimary ? "4" : "3"}
                checkBoxText={
                  "The address associated with this credit card is the same as my primary address"
                }
                checkBoxName={"sameAsPrimary"}
                addressLineTwo={achSell?.primaryResidence?.street2}
                changeReduxField={change}
              />
            </FormSection>
            <SellFormDesktopBottomButton
              reduxFormDispatch={dispatch}
              valid={valid}
              missingInfo={
                missingInfo ||
                !achCompleted() ||
                !verifyCC(achSell?.verification, cardKnoxState)
              }
            />
          </div>
          <div>
            <SellOrderSummary
              reduxFormDispatch={dispatch}
              valid={valid}
              missingInfo={
                missingInfo ||
                !achCompleted() ||
                !verifyCC(achSell?.verification, cardKnoxState)
              }
            />
            <SecurityImages />
          </div>
        </MainContentSellContainer>
      )}
    </form>
  );
};

export default reduxForm({
  form: "achSell"
})(ACHSellWorkflow);
